import { ItemsCountCell } from "app/pages/sales-intelligence/common-components/table-cells/components/ItemsCountCell/ItemsCountCell";
import type { LeadsSearchRow } from "app/pages/sales-intelligence/modules/leads-search";
import { TabName } from "app/pages/sales-intelligence/sub-modules/account-overview/company/types/tabs";
import { COLUMN_NEWS_NAME } from "app/pages/sales-intelligence/sub-modules/signals/constants";
import { StyledCenteredBaseCell } from "app/pages/sales-intelligence/sub-modules/signals/containers/cells/SignalNewsCellContainer/styles";
import { useNewsSignalsTooltipModel } from "app/pages/sales-intelligence/sub-modules/signals/hooks/cells/useNewsSignalsTooltipModel";
import { useSignalTooltipData } from "app/pages/sales-intelligence/sub-modules/signals/hooks/cells/useSignalTooltipData";
import { useSignalTooltipDelayed } from "app/pages/sales-intelligence/sub-modules/signals/hooks/cells/useSignalTooltipDelayed";
import type {
    IUseTrackedSignalTooltipResponse } from "app/pages/sales-intelligence/sub-modules/signals/hooks/cells/useTrackedSignalTooltip";
import {
    useTrackedSignalTooltip,
} from "app/pages/sales-intelligence/sub-modules/signals/hooks/cells/useTrackedSignalTooltip";
import useTooltipDelayedState from "app/pages/sales-intelligence/sub-modules/signals/hooks/signals-tooltip/useTooltipDelayedState";
import { getCompanyOverviewLink } from "app/pages/sales-intelligence/sub-modules/signals/utils/signalCompanyOverviewLink";
import { getSignalsCountFromRow } from "app/pages/sales-intelligence/sub-modules/signals/utils/tableColumns";

import { SignalsEventsTooltipContainer } from "../SignalsEventsTooltipContainer/SignalsEventsTooltipContainer";

interface ISignalNewsCellContainerProps<V extends LeadsSearchRow> {
    record: V;
}

export const SignalNewsCellContainer = <V extends LeadsSearchRow>({ record }: ISignalNewsCellContainerProps<V>) => {
    const newsValue = record[COLUMN_NEWS_NAME] ?? [];
    const { redirectLink, getSignalTypeLink } = getCompanyOverviewLink({ activePageSection: TabName.signals, record });
    const {
        onTrackedTooltipClick,
        onTrackedTooltipOpen,
        onTrackedSignalTypeClick,
        onTrackedSignalArticleClick,
    }: IUseTrackedSignalTooltipResponse = useTrackedSignalTooltip({
        field: COLUMN_NEWS_NAME,
        value: newsValue.length,
    });
    const tooltipDelayedState = useTooltipDelayedState();
    const { fetchStatus, signalsDescription, signalsGroupedByType, signalEvents, companyInfo } =
        useSignalTooltipData<LeadsSearchRow>({
            isEnabled: tooltipDelayedState.isTooltipEnabled,
            row: record,
        });

    const createViewModel = useNewsSignalsTooltipModel({
        signalsDescription,
        signalsGroupedByType,
        signalEvents,
        companyInfo,
    });

    const { onMouseLeave, onMouseEnter } = useSignalTooltipDelayed({
        onTooltipOpen: onTrackedTooltipOpen,
        tooltipDelayedState,
    });

    return (
        <SignalsEventsTooltipContainer
            row={record}
            isEnabled={tooltipDelayedState.isTooltipEnabled}
            redirectLink={redirectLink}
            fetchStatus={fetchStatus}
            getSignalTypeLink={getSignalTypeLink}
            onViewAll={onTrackedTooltipClick}
            onSignalTypeClick={onTrackedSignalTypeClick}
            onSignalArticleClick={onTrackedSignalArticleClick}
            createViewModel={createViewModel}
        >
            <StyledCenteredBaseCell>
                <ItemsCountCell
                    value={getSignalsCountFromRow(record)}
                    onMouseLeave={onMouseLeave}
                    onMouseEnter={onMouseEnter}
                />
            </StyledCenteredBaseCell>
        </SignalsEventsTooltipContainer>
    );
};
